import { formatShortDate, isShortDate, parseShortDate } from "@shared";
import { isBefore } from "date-fns";

import { ClientSearchResult } from "@/api";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { LanguageType } from "@/translations";

interface ClientSearchArgs {
  extendedSearchName?: string;
  extendedSearchBirthday?: Date;
}

/**
 * Earliest plausible birthday. Guards against partial-year input like "10.08.20", which date-fns
 * parses to year 20 — not a real birthday, and rejected by the backend's Birthday validation.
 */
const EARLIEST_BIRTHDAY = new Date(1900, 0, 1);

/**
 * Routes free-text input to the right client-search filter: a complete short date searches by
 * Geburtsdatum, anything else by name. Name uses the extended-name filter (not searchText, which
 * never matches the first name), so a first name also finds results.
 */
export function buildClientSearchArgs(input: string, language: LanguageType): ClientSearchArgs {
  const trimmed = input.trim();
  if (!trimmed) {
    return {};
  }

  if (isShortDate(trimmed, locales[language])) {
    const birthday = parseShortDate(trimmed, locales[language]);
    if (!isBefore(birthday, EARLIEST_BIRTHDAY)) {
      return { extendedSearchBirthday: birthday };
    }
  }

  return { extendedSearchName: trimmed };
}

export function formatClientOptionLabel(option: ClientSearchResult): string {
  return option.fullName?.trim() ?? `${option.lastName}, ${option.firstName}`;
}

export function formatClientOptionCaption(
  option: ClientSearchResult,
  language: LanguageType,
  bornOnPrefix: string
): string {
  const parts: string[] = [];

  if (option.birthday) {
    parts.push(`${bornOnPrefix} ${formatShortDate(option.birthday, locales[language])}`);
  }

  if (option.caseNumber) {
    parts.push(option.caseNumber);
  }

  return parts.join(", ");
}
